import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, GradientButton, CTABanner } from '../../components/common/Primitives';
import ModuleCard from '../../components/common/ModuleCard';
import VideoLightbox from '../../components/common/VideoLightbox';
import { SP_EXPERIENCES } from '../../lib/mockStudent';
import { FEATURED_VIDEO_ID, youtubeThumb } from '../../lib/moduleVideos';
import { PlayCircle } from 'lucide-react';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import GyanMitraSection from '../../components/common/GyanMitraSection';

export default function StudentExperiences() {
  const d = SP_EXPERIENCES;
  const [heroOpen, setHeroOpen] = useState(false);

  return (
    <PageShell>
      {/* HERO with video placeholder */}
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[54px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight text="No software licence fee through June 2027" /></div>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1.5 text-[11.5px] font-bold tracking-widest text-purple">VR · {d.hero.pill}</div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-cyan-50 p-6 md:p-8 shadow-soft">
              <button type="button" onClick={() => setHeroOpen(true)} data-testid="student-exp-hero-video-play" aria-label="Play learning experiences preview" className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30">
                <img src={youtubeThumb(FEATURED_VIDEO_ID, 'maxresdefault')} alt="Learning experiences preview" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center"><PlayCircle className="h-14 w-14 text-white" /></span>
              </button>
              <div className="mt-4 text-[11.5px] font-bold tracking-widest text-purple">LEARNING EXPERIENCES</div>
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.curriculum.eyebrow} title={d.curriculum.title} desc={d.curriculum.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.curriculum.items.map((m,i)=>(<ModuleCard key={i} {...m} />))}
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section className="px-6 py-12 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.pipeline.eyebrow} title={d.pipeline.title} desc={d.pipeline.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.pipeline.items.map((m,i)=>(<ModuleCard key={i} {...m} />))}
          </div>
        </div>
      </section>

      {/* EXPLORATION */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.exploration.eyebrow} title={d.exploration.title} desc={d.exploration.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.exploration.items.map((m,i)=>(<ModuleCard key={i} {...m} />))}
          </div>
        </div>
      </section>

      <GyanMitraSection />

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title="Ready to bring these experiences home?" desc="Request individual or family access to the complete Gyan Dhara learning ecosystem." primary={{ label: 'Request Access', to: '/access-request' }} secondary={{ label: 'How Learning Works', to: '/student-parent/how-it-works' }} />
        </div>
      </section>

      {heroOpen && <VideoLightbox videoId={FEATURED_VIDEO_ID} title="Learning experiences" onClose={() => setHeroOpen(false)} />}
    </PageShell>
  );
}
